import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { PartnerDriverProfileCard } from 'src/components/partner-driver/profile/PartnerDriverProfileCard'
import type { PartnerDriverDetails, PartnerDriverSearchRow } from 'src/interfaces/partnerDriver'
import { BackgroundChecksCard } from './BackgroundChecksCard'
import { DeleteContractorCard } from './DeleteContractorCard'
import { OnboardingStatusCard } from './OnboardingStatusCard'
import { PartnerDriverHeader } from './PartnerDriverHeader'
import { StepsLinkCard } from './StepsLinkCard'
import { TemplateCard } from './TemplateCard'

interface Props {
  driver: PartnerDriverDetails
  summary: PartnerDriverSearchRow | null
  summaryLoading: boolean
  summaryError: string | null
  backTo: { pathname: string; search: string }
  /** The route's navigation state, forwarded to the steps page so its Back links keep the search. */
  navigationState: unknown
  /** Refetch the driver after a write that changes steps, template or status. */
  onDriverChanged(): void
  /** Refetch the driver and its summary — a profile edit can change the OTP/Yardstik row too. */
  onProfileSaved(): void
  onRetrySummary(): void
}

/**
 * Two columns under `lg`'s breakpoint fall back to one. Profile (the tallest card) takes the left
 * column with the link to the steps page under it; the status-style cards stack on the
 * right, with the destructive Delete contractor card last.
 * `items-start` so the shorter column does not stretch its cards.
 */
const PartnerDriverDetailsContentComponent = ({
  driver,
  summary,
  summaryLoading,
  summaryError,
  backTo,
  navigationState,
  onDriverChanged,
  onProfileSaved,
  onRetrySummary,
}: Props) => {
  return (
    <Fragment>
      <PartnerDriverHeader driver={driver} backTo={backTo} backLabel="Back to search" />
      <div css={tw`grid grid-cols-1 items-start gap-6 lg:grid-cols-2`}>
        <div css={tw`flex min-w-0 flex-col gap-6`}>
          <PartnerDriverProfileCard
            driver={driver}
            otpCode={summary?.otpCode ?? null}
            onSaved={onProfileSaved}
          />
          <StepsLinkCard driver={driver} navigationState={navigationState} />
        </div>
        <div css={tw`flex min-w-0 flex-col gap-6`}>
          <BackgroundChecksCard
            summary={summary}
            isLoading={summaryLoading}
            error={summaryError}
            onRetry={onRetrySummary}
          />
          <OnboardingStatusCard driver={driver} onChanged={onDriverChanged} />
          <TemplateCard driver={driver} onChanged={onDriverChanged} />
          <DeleteContractorCard driver={driver} backTo={backTo} />
        </div>
      </div>
    </Fragment>
  )
}

export const PartnerDriverDetailsContent = memo(PartnerDriverDetailsContentComponent)
